import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useFetcher, useNavigate, useRouteLoaderData } from '@remix-run/react';
import { useIntercom } from 'react-use-intercom';
import { useDisableBodyScroll } from '../../hooks/use-disable-body-scroll';
import { inquiryModal } from '../../state/inquiry-modal';
import { useSnapshot } from 'valtio';
import { useTranslation } from 'react-i18next';
import { AccommodationType, accommodationOptions } from '../../constants/accommodation';
import { phoneCodes } from '@kumbh/booking-components';
import phoneValidator from 'phone';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '../ui/form';
import { testId } from '../ui/test-id';
import {
  inquirySchema,
  InquiryFormValues,
  calculateCapacity,
  calculateTotalRooms,
} from './inquiry-schema';
import type { loader as rootLoader } from '../../root';
import { NivaasOffering, getOfferingFullRoomPrice } from '../../types/offerings';
import { displayPrice } from '../../state/prices';
import { environmentState } from '../../state/environment';

// Additional bed prices in INR per room type (per night)
const ADDITIONAL_BED_PRICES: Record<string, number> = {
  'premium-twin-bed-cottage': 7000,
  mudhouse: 9600,
};

// Map accommodation types to nivaas offering slugs
const ACCOMMODATION_TO_SLUG: Record<AccommodationType, string> = {
  mudhouse: 'mudhouse',
  'mudhouse-dormitory': 'mudhouse-dormitory',
  'premium-twin-bed-cottage': 'premium-twin-bed-cottage',
};

// Get default country code based on detected country
const getDefaultCountryCode = () => {
  const countryCode = environmentState.countryCode || 'IN';
  const country = phoneCodes.find((c) => c.c === countryCode);
  return country?.d || '+91';
};

interface GetInTouchModalProps {
  isOpen: boolean;
  onClose: VoidFunction;
}

export const GetInTouchModal: React.FC<GetInTouchModalProps> = ({ isOpen, onClose }) => {
  useDisableBodyScroll(isOpen);
  const { update: updateIntercom } = useIntercom();
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'home']);
  const { initialInquiry, preferences } = useSnapshot(inquiryModal);
  const [countryCode, setCountryCode] = useState(getDefaultCountryCode);
  const [isCountryDropdownVerbose, setIsCountryDropdownVerbose] = useState(false);

  // Get offerings data from root loader
  const rootData = useRouteLoaderData<typeof rootLoader>('root');
  const offerings = useMemo(
    () => (rootData?.offerings || []) as NivaasOffering[],
    [rootData?.offerings],
  );

  // Create a map of slug -> price for quick lookup (full room price)
  const offeringPrices = useMemo(() => {
    const priceMap: Record<string, string | null> = {};
    for (const option of accommodationOptions) {
      const slug = ACCOMMODATION_TO_SLUG[option.value];
      const offering = offerings.find((o) => o.slug === slug);
      const price = getOfferingFullRoomPrice(offering);
      priceMap[option.value] = price ? displayPrice({ priceInINR: price.amount }) : null;
    }
    return priceMap;
  }, [offerings]);

  // Hide Intercom launcher when modal is open
  useEffect(() => {
    updateIntercom({ hideDefaultLauncher: isOpen });
  }, [isOpen, updateIntercom]);

  const form = useForm<InquiryFormValues>({
    resolver: zodResolver(inquirySchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      accommodations: {},
      additionalBeds: 0,
      checkIn: '',
      checkOut: '',
      guests: '',
      message: initialInquiry ?? '',
    },
  });

  const accommodations = form.watch('accommodations');
  const additionalBeds = form.watch('additionalBeds');
  const guests = form.watch('guests');

  // Calculate capacities
  const totalRooms = useMemo(() => calculateTotalRooms(accommodations), [accommodations]);
  // Only mudhouse and premium-twin-bed-cottage can have additional beds (not dormitory)
  const maxAdditionalBeds = useMemo(() => {
    return (
      (accommodations['mudhouse'] ?? 0) +
      (accommodations['premium-twin-bed-cottage'] ?? 0)
    );
  }, [accommodations]);
  const totalCapacity = useMemo(
    () => calculateCapacity(accommodations, additionalBeds),
    [accommodations, additionalBeds],
  );
  const baseCapacity = useMemo(
    () => calculateCapacity(accommodations, 0),
    [accommodations],
  );

  // Calculate estimated nightly price from offerings (full room prices)
  // For additional beds, we use a weighted average based on selected room types
  const estimatedNightlyPrice = useMemo(() => {
    let total = 0;
    let additionalBedTotal = 0;

    // Count rooms that can have additional beds and their costs
    const premiumCount = accommodations['premium-twin-bed-cottage'] ?? 0;
    const mudhouseCount = accommodations['mudhouse'] ?? 0;
    const totalBedsCapacity = premiumCount + mudhouseCount;

    for (const option of accommodationOptions) {
      const count = accommodations[option.value] ?? 0;
      if (count > 0) {
        const slug = ACCOMMODATION_TO_SLUG[option.value];
        const offering = offerings.find((o) => o.slug === slug);
        const price = getOfferingFullRoomPrice(offering);
        if (price) {
          total += price.amount * count;
        }
      }
    }

    // Estimate additional bed cost (use weighted average if both room types selected)
    if (additionalBeds > 0 && totalBedsCapacity > 0) {
      const premiumBedPrice = ADDITIONAL_BED_PRICES['premium-twin-bed-cottage'];
      const mudhouseBedPrice = ADDITIONAL_BED_PRICES['mudhouse'];

      if (premiumCount > 0 && mudhouseCount === 0) {
        additionalBedTotal = additionalBeds * premiumBedPrice;
      } else if (mudhouseCount > 0 && premiumCount === 0) {
        additionalBedTotal = additionalBeds * mudhouseBedPrice;
      } else {
        // Mixed selection - use weighted average
        const avgPrice =
          (premiumCount * premiumBedPrice + mudhouseCount * mudhouseBedPrice) /
          totalBedsCapacity;
        additionalBedTotal = additionalBeds * avgPrice;
      }
    }

    return total + additionalBedTotal;
  }, [accommodations, additionalBeds, offerings]);

  const guestCount = parseInt(guests || '0') || 0;
  // Only show over-capacity warning when accommodation is selected
  const isOverCapacity = totalRooms > 0 && guestCount > totalCapacity;

  // Pre-fill from preferences
  useEffect(() => {
    if (preferences) {
      if (preferences.checkIn) form.setValue('checkIn', preferences.checkIn);
      if (preferences.checkOut) form.setValue('checkOut', preferences.checkOut);
      if (preferences.guests) form.setValue('guests', String(preferences.guests));
      if (preferences.accommodationType) {
        form.setValue('accommodations', {
          [preferences.accommodationType as AccommodationType]: 1,
        });
      }
    }
  }, [preferences, form]);

  // Update message when initialInquiry changes
  useEffect(() => {
    if (initialInquiry) {
      form.setValue('message', initialInquiry);
    }
  }, [initialInquiry, form]);

  // Cap guest count at capacity when accommodation is selected
  useEffect(() => {
    // Only cap guests if accommodation is selected AND guests exceed capacity
    if (totalRooms > 0 && guestCount > totalCapacity) {
      form.setValue('guests', String(totalCapacity));
    }
    // When accommodation is removed, keep the current guest count (don't reset)
  }, [totalRooms, totalCapacity, guestCount, form]);

  // Reset additional beds if rooms are removed
  useEffect(() => {
    if (additionalBeds > maxAdditionalBeds) {
      form.setValue('additionalBeds', maxAdditionalBeds);
    }
  }, [additionalBeds, maxAdditionalBeds, form]);

  // Update accommodation quantity
  const updateAccommodationQuantity = useCallback(
    (type: AccommodationType, delta: number) => {
      const currentAccommodations = form.getValues('accommodations');
      const currentCount = currentAccommodations[type] ?? 0;
      const newCount = Math.max(0, Math.min(10, currentCount + delta));

      if (newCount === 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [type]: _removed, ...rest } = currentAccommodations;
        form.setValue('accommodations', rest);
      } else {
        const newSelection = { ...currentAccommodations, [type]: newCount };
        form.setValue('accommodations', newSelection);

        // Auto-fill guests with total capacity when adding rooms
        if (delta > 0) {
          const newCapacity = calculateCapacity(newSelection, additionalBeds);
          form.setValue('guests', String(newCapacity));
        }
      }
    },
    [form, additionalBeds],
  );

  // Update additional beds
  const updateAdditionalBeds = useCallback(
    (delta: number) => {
      const currentBeds = form.getValues('additionalBeds');
      const newBeds = Math.max(0, Math.min(currentBeds + delta, maxAdditionalBeds));
      form.setValue('additionalBeds', newBeds);

      // Auto-update guest count when beds increase
      if (delta > 0) {
        const currentGuests = parseInt(form.getValues('guests') || '0') || 0;
        form.setValue('guests', String(currentGuests + delta));
      }
    },
    [form, maxAdditionalBeds],
  );

  // Handle success - redirect to thank you page
  useEffect(() => {
    const data = fetcher.data as { success: boolean; inquiryId?: string } | undefined;
    if (data?.success && data?.inquiryId) {
      onClose();
      navigate(`/inquiry/${btoa(data.inquiryId)}`);
    }
  }, [fetcher.data, navigate, onClose]);

  const onSubmit = useCallback(
    (values: InquiryFormValues) => {
      if (isOverCapacity) return;

      // Combine country code with phone number
      const fullPhone = values.phone.startsWith('+')
        ? values.phone
        : `${countryCode}${values.phone.replace(/^0+/, '')}`;

      // Validate combined phone number
      if (!phoneValidator(fullPhone).isValid) {
        form.setError('phone', {
          type: 'manual',
          message: 'Please enter a valid phone number',
        });
        return;
      }

      // Build supplementary items for additional beds
      const supplementaryItems =
        values.additionalBeds > 0
          ? JSON.stringify([
              {
                name: 'Additional Bed',
                price: {
                  amount: ADDITIONAL_BED_PRICES['premium-twin-bed-cottage'],
                  currency: 'INR',
                },
                quantity: values.additionalBeds,
                gst: 0,
              },
            ])
          : undefined;

      // Convert accommodations to array format for backend
      const accommodationTypesArray = Object.entries(values.accommodations)
        .filter(([, count]) => (count ?? 0) > 0)
        .flatMap(([type, count]) => Array(count).fill(type));

      // Build message with additional beds info included (as fallback)
      let finalMessage = values.message || '';
      if (values.additionalBeds > 0) {
        const bedsNote = `\n\n[Additional Beds Requested: ${values.additionalBeds}]`;
        finalMessage = finalMessage ? finalMessage + bedsNote : bedsNote.trim();
      }

      // Submit structured data
      fetcher.submit(
        {
          name: values.name,
          email: values.email,
          phone: fullPhone,
          accommodationTypes: JSON.stringify(accommodationTypesArray),
          checkIn: values.checkIn || '',
          checkOut: values.checkOut || '',
          guests: values.guests || '',
          message: finalMessage,
          ...(supplementaryItems && { supplementaryItems }),
        },
        { method: 'post', action: '/?index' },
      );
    },
    [fetcher, isOverCapacity, countryCode, form],
  );

  const onReset = useCallback(() => {
    form.reset();
  }, [form]);

  const handleBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleBackdropKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  if (!isOpen) return null;

  const isSubmitting = fetcher.state === 'submitting';

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Close modal"
        {...testId('inquiry-backdrop')}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm cursor-pointer"
        onClick={handleBackdropClick}
        onKeyDown={handleBackdropKeyDown}
      />

      {/* Modal */}
      <div
        {...testId('inquiry-modal')}
        className="relative w-full sm:max-w-2xl max-h-[95dvh] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl animate-in fade-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600 px-4 sm:px-6 py-4 sm:py-5 rounded-t-2xl">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            {...testId('inquiry-close-button')}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <div className="flex items-center gap-3 pr-8">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-display font-bold text-white truncate">
                {t('home:get-in-touch.title')}
              </h2>
              <p className="text-emerald-100 text-xs sm:text-sm mt-0.5">
                Magh Mela 2026 · Prayagraj
              </p>
            </div>
          </div>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="p-4 sm:p-6 space-y-5 sm:space-y-6"
          >
            {/* Accommodation Selection - Quantity-based */}
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wider">
                  {t('home:get-in-touch.accommodation-interest')}
                </h3>
                {totalRooms > 0 && (
                  <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded-full whitespace-nowrap">
                    {totalRooms} {totalRooms === 1 ? 'room' : 'rooms'}
                  </span>
                )}
              </div>

              <div className="space-y-2">
                {accommodationOptions.map((option) => {
                  const count = accommodations[option.value] ?? 0;
                  const isSelected = count > 0;
                  return (
                    <div
                      key={option.value}
                      {...testId(`inquiry-tent-${option.value}`)}
                      className={clsx(
                        'flex items-center justify-between p-3 sm:p-4 rounded-xl border-2 transition-all gap-2',
                        isSelected
                          ? 'bg-emerald-50 border-emerald-300'
                          : 'bg-gray-50 border-transparent hover:border-gray-200',
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <span
                          className={clsx(
                            'font-semibold block text-sm sm:text-base truncate',
                            isSelected ? 'text-emerald-800' : 'text-gray-800',
                          )}
                        >
                          {option.label}
                        </span>
                        <span className="text-xs sm:text-sm text-gray-500 block mt-0.5 line-clamp-2">
                          {option.description}
                        </span>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <svg
                              className="w-3 h-3 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                            {option.capacity} guests
                          </span>
                          {offeringPrices[option.value] && (
                            <>
                              <span className="text-gray-300">•</span>
                              <span className="text-xs font-semibold text-emerald-600">
                                {offeringPrices[option.value]}/night
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                        <button
                          type="button"
                          {...testId(`inquiry-tent-${option.value}-decrement`)}
                          onClick={() => updateAccommodationQuantity(option.value, -1)}
                          disabled={count === 0}
                          className={clsx(
                            'w-9 h-9 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all text-lg font-medium',
                            count === 0
                              ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                              : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200 active:bg-emerald-300',
                          )}
                        >
                          −
                        </button>
                        <span
                          className={clsx(
                            'w-6 sm:w-8 text-center font-semibold text-sm sm:text-base',
                            isSelected ? 'text-emerald-700' : 'text-gray-400',
                          )}
                        >
                          {count}
                        </span>
                        <button
                          type="button"
                          {...testId(`inquiry-tent-${option.value}-increment`)}
                          onClick={() => updateAccommodationQuantity(option.value, 1)}
                          className="w-9 h-9 sm:w-8 sm:h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600 active:bg-emerald-700 transition-all text-lg font-medium"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Capacity and Price Info */}
              {totalRooms > 0 && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 space-y-2">
                  <p className="text-sm text-teal-700">
                    {additionalBeds > 0
                      ? `Base capacity: ${baseCapacity} guests + ${additionalBeds} extra bed(s) = ${totalCapacity} guests total`
                      : `Your selection fits up to ${baseCapacity} guests`}
                  </p>
                  {estimatedNightlyPrice > 0 && (
                    <p className="text-sm font-semibold text-teal-800">
                      Estimated: {displayPrice({ priceInINR: estimatedNightlyPrice })}
                      /night
                    </p>
                  )}
                </div>
              )}

              {/* Additional Beds - Only show when rooms are selected */}
              {totalRooms > 0 && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex-1">
                    <span className="font-medium text-amber-800">
                      {t('home:get-in-touch.extra-bed')}
                    </span>
                    <span className="block text-xs text-amber-600 mt-0.5">
                      ₹7,000-9,600/bed/night • Max {maxAdditionalBeds} (1 per room)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => updateAdditionalBeds(-1)}
                      disabled={additionalBeds === 0}
                      className={clsx(
                        'w-8 h-8 rounded-full flex items-center justify-center transition-all text-lg font-medium',
                        additionalBeds === 0
                          ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                          : 'bg-amber-200 text-amber-700 hover:bg-amber-300',
                      )}
                    >
                      −
                    </button>
                    <span
                      className={clsx(
                        'w-8 text-center font-semibold',
                        additionalBeds > 0 ? 'text-amber-700' : 'text-gray-400',
                      )}
                    >
                      {additionalBeds}
                    </span>
                    <button
                      type="button"
                      onClick={() => updateAdditionalBeds(1)}
                      disabled={additionalBeds >= maxAdditionalBeds}
                      className={clsx(
                        'w-8 h-8 rounded-full flex items-center justify-center transition-all text-lg font-medium',
                        additionalBeds >= maxAdditionalBeds
                          ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                          : 'bg-amber-500 text-white hover:bg-amber-600',
                      )}
                    >
                      +
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Dates Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <FormField
                control={form.control}
                name="checkIn"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs sm:text-sm">
                      {t('home:get-in-touch.checkin-date')}
                    </FormLabel>
                    <FormControl>
                      <input
                        {...field}
                        id="ks-checkin"
                        type="date"
                        min="2025-12-31"
                        max="2026-02-20"
                        className="w-full max-w-full box-border px-3 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="checkOut"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs sm:text-sm">
                      {t('home:get-in-touch.checkout-date')}
                    </FormLabel>
                    <FormControl>
                      <input
                        {...field}
                        id="ks-checkout"
                        type="date"
                        min={form.watch('checkIn') || '2025-12-31'}
                        max="2026-02-20"
                        className="w-full max-w-full box-border px-3 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Guests */}
            <FormField
              control={form.control}
              name="guests"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs sm:text-sm">
                    {t('home:get-in-touch.number-of-guests')}
                  </FormLabel>
                  <FormControl>
                    <input
                      {...field}
                      id="ks-guests"
                      type="number"
                      min="1"
                      max={totalRooms > 0 ? totalCapacity : undefined}
                      placeholder={t('home:get-in-touch.how-many')}
                      className={clsx(
                        'w-full px-3 py-3 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base',
                        isOverCapacity ? 'border-red-400 bg-red-50' : 'border-gray-300',
                      )}
                    />
                  </FormControl>
                  {isOverCapacity && (
                    <p className="text-xs text-red-600">
                      {t('home:get-in-touch.capacity-exceeded', { max: totalCapacity })}
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <hr className="border-gray-200" />

            {/* Contact Information */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wider">
                {t('home:get-in-touch.contact-details')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">
                        {t('home:get-in-touch.full-name')} *
                      </FormLabel>
                      <FormControl>
                        <input
                          {...field}
                          id="ks-name"
                          type="text"
                          placeholder={t('home:get-in-touch.placeholder.name')}
                          className={clsx(
                            'w-full px-3 py-3 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base',
                            form.formState.errors.name
                              ? 'border-red-400'
                              : 'border-gray-300',
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">
                        {t('home:get-in-touch.email')} *
                      </FormLabel>
                      <FormControl>
                        <input
                          {...field}
                          id="ks-email"
                          type="email"
                          placeholder={t('home:get-in-touch.placeholder.email')}
                          className={clsx(
                            'w-full px-3 py-3 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base',
                            form.formState.errors.email
                              ? 'border-red-400'
                              : 'border-gray-300',
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs sm:text-sm">
                      {t('home:get-in-touch.phone')} (with country code) *
                    </FormLabel>
                    <FormControl>
                      <div
                        className={clsx(
                          'flex items-center border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-emerald-500',
                          form.formState.errors.phone
                            ? 'border-red-400'
                            : 'border-gray-300',
                        )}
                      >
                        <select
                          value={countryCode}
                          onChange={(e) => {
                            setCountryCode(e.target.value);
                            setIsCountryDropdownVerbose(false);
                          }}
                          onPointerDownCapture={() => setIsCountryDropdownVerbose(true)}
                          onBlurCapture={() => setIsCountryDropdownVerbose(false)}
                          className="h-full px-2 py-3 sm:py-2.5 bg-gray-50 border-r border-gray-300 text-sm focus:outline-none"
                        >
                          {phoneCodes.map((country) => (
                            <option key={country.c} value={country.d}>
                              {isCountryDropdownVerbose
                                ? `${country.e} ${country.n}`
                                : `${country.e} ${country.d}`}
                            </option>
                          ))}
                        </select>
                        <input
                          {...field}
                          id="ks-phone"
                          type="tel"
                          placeholder={t('home:get-in-touch.placeholder.phone')}
                          className="flex-1 px-3 py-3 sm:py-2.5 text-base focus:outline-none"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Additional Message */}
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs sm:text-sm">
                    {t('home:get-in-touch.additional-message')}
                  </FormLabel>
                  <FormControl>
                    <textarea
                      {...field}
                      id="ks-message"
                      placeholder={t('home:get-in-touch.placeholder.inquiry')}
                      rows={3}
                      className="w-full px-3 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none text-base"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2 pb-2 sm:pb-0">
              <button
                type="button"
                onClick={onReset}
                className="py-3 sm:py-3.5 px-6 rounded-xl font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors"
              >
                {t('common:reset')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting || isOverCapacity}
                className={clsx(
                  'flex-1 py-3.5 sm:py-3.5 px-6 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2',
                  isSubmitting || isOverCapacity
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 active:from-emerald-700 active:to-teal-700 shadow-lg hover:shadow-xl',
                )}
                {...testId('express-interest-submit-button')}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    {t('home:get-in-touch.submitting')}
                  </>
                ) : (
                  <>
                    {t('home:get-in-touch.submit-interest')}
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </>
                )}
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1 pb-safe">
              <svg
                className="w-4 h-4 text-emerald-500 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              {t('home:get-in-touch.secure-info')}
            </p>
          </form>
        </Form>
      </div>
    </div>
  );
};
